import React, { useEffect, useState } from 'react'
import { callAPI, formatComma } from 'helpers'
import { get, groupBy, find, sum, sumBy, uniq } from 'lodash'
import moment, { isDate } from 'moment'
import theme from 'theme'

// Components
import styled from 'styled-components'
import { Table, DatePicker } from 'antd'
import { SectionContent, SectionHead, SectionBody } from './BasicComponents'

// JSON
import provinces from 'data/provinces.json'

// Hooks
import { useEditable } from 'hooks/useEditable'

const SectionOrdersByCourseType = () => {
  useEditable()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])

  const [startMonth, setStartMonth] = useState(moment().add(-11, 'month').format('YYYY-MM'))
  const [endMonth, setEndMonth] = useState(moment().format('YYYY-MM'))

  const columns = [
    { title: 'รหัสคอร์ส', key: 'courseCode', dataIndex: 'courseCode' },
    { title: 'รอบสด', key: 'offline', align: 'center', render: ({ rowKey, offline }) => !offline ? <div>0</div> : <div>{formatComma(offline)}</div> },
    { title: 'ออนไลน์', key: 'online', align: 'center', render: ({ rowKey, online }) => !online ? <div>0</div> : <div>{formatComma(online)}</div> },
    { title: 'รวมทั้งหมด', key: 'total', align: 'center', render: ({ rowKey, offline, online }) => <div>{formatComma(offline + online)}</div> }
  ]

  useEffect(() => {
    const fetchAggregation = async () => {
      try {
        setLoading(true)
        const { result } = await callAPI({
          url: '/analytics/orders-by-course-type',
          query: {
            startMonth,
            endMonth
          }
        })

        const resultByCourseCode = groupBy(result, 'courseCode')
        const data = Object.keys(resultByCourseCode)
          .map(courseCode => {
            const counts = resultByCourseCode[courseCode]
            const offlineRows = counts.filter(({ courseType }) => courseType === 'offline')
            const offlineCount = sumBy(offlineRows, 'count')
            const onlineRows = counts.filter(({ courseType }) => courseType === 'online')
            const onlineCount = sumBy(onlineRows, 'count')

            const monthKeys = uniq(counts.map(({ ym }) => ym), 'ym').sort((a, b) => b.localeCompare(a))
            return {
              courseCode,
              rowKey: courseCode,
              offline: offlineCount,
              online: onlineCount,
              total: offlineCount + onlineCount,
              children: monthKeys.map(ym => ({
                rowKey: `${courseCode}___${ym}`,
                courseCode: moment(ym).add(543, 'years').format('MMM YY'),
                offline: get(find(offlineRows, row => row.ym === ym), 'count') || 0,
                online: get(find(onlineRows, row => row.ym === ym), 'count') || 0
              }))
            }
          })
          .sort((a, b) => b.total - a.total)
          .slice()

        setData(data)
        setLoading(false)
      } catch (error) {
        console.error(new Error(`Error while trying to fetch order aggregation by course type: ${error.message}.`))
        setLoading(false)
      }
    }

    fetchAggregation()
  }, [startMonth, endMonth])

  const sumRow = data.reduce((row, { offline, online }) => ({
    ...row,
    offline: row.offline + offline,
    online: row.online + online
  }), { rowKey: 'sum', courseCode: 'รวมทั้งหมด', offline: 0, online: 0 })

  return (
    <ContentWrapper>
      <SectionContent>
        <SectionHead style={{ position: 'relative' }}>
          <div>ตารางเปรียบเทียบการสมัครคอร์สเรียนรอบสด/ออนไลน์</div>
          <RangePickerWrapper>
            <DatePicker.MonthPicker
              style={{ width: 110, display: 'inline-block', verticalAlign: 'top' }}
              format='MMM YYYY'
              disabledDate={current => current && (current < moment('2020-04', 'YYYY-MM') || current > moment().endOf('day'))}
              onChange={(_date) => setStartMonth(_date.format('YYYY-MM'))}
              value={moment(startMonth, 'YYYY-MM')} />
            <Hyphen>-</Hyphen>
            <DatePicker.MonthPicker
              style={{ width: 110, display: 'inline-block', verticalAlign: 'top' }}
              format='MMM YYYY'
              disabledDate={current => current && (current < moment('2020-04', 'YYYY-MM') || current > moment().endOf('day'))}
              onChange={(_date) => setEndMonth(_date.format('YYYY-MM'))}
              value={moment(endMonth, 'YYYY-MM')} />
          </RangePickerWrapper>
        </SectionHead>
        <SectionBody>
          <TableWrapper>
            <Table
              rowKey={({ rowKey }) => rowKey}
              loading={loading}
              columns={columns}
              dataSource={data}
              pagination={false}
              footer={() =>
                <Footer>
                  <FooterCol style={{ textAlign: 'left' }}>
                    <strong>รวมทั้งหมด</strong>
                  </FooterCol>
                  <FooterCol>
                    {formatComma(sumRow.offline)}
                  </FooterCol>
                  <FooterCol>
                    {formatComma(sumRow.online)}
                  </FooterCol>
                  <FooterCol>
                    {formatComma(sum([sumRow.offline, sumRow.online]))}
                  </FooterCol>
                </Footer>
              }
              scroll={{ y: 360 }} />
          </TableWrapper>
        </SectionBody>
      </SectionContent>

    </ContentWrapper>
  )
}

export default SectionOrdersByCourseType

const ContentWrapper = styled.section`
  background-color: white;
  margin-top: 20px;
`
const TableWrapper = styled.div`
  .ant-table-footer {
    padding: 16px 0px !important;
  }
`
const Clickable = styled.div`
  cursor: pointer;
  transition: color 0.3s ease-in-out;
  &:hover {
    color: ${theme.colors.secondary};
    text-decoration: underline;
  }
`
const Footer = styled.div`
  display: flex;
`
const FooterCol = styled.div`
  flex: 1;
  padding: 0px 16px;
  text-align: center;
`
const RangePickerWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`
const Hyphen = styled.div`
  display: inline-block;
  vertical-align: top;
  width: 20px;
  text-align: center;
  font-weight: 400;
`
